import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTabActive } from "../../lib/tabActive";
import { useTabStore } from "../../lib/tabStore";

/**
 * TOPLU GİRİŞ SAYFALARININ ORTAK ALTYAPISI (Toplu Fazla Mesai + Toplu Devamsızlık).
 *
 * İki sayfa da uygulama içi SEKME olarak açılır; dönem yönetimi ve sekme kapatma
 * davranışları birebir aynıdır ve tek yerde durur.
 */

const pad = (n: number) => String(n).padStart(2, "0");

/** "YYYY-MM" → geçerli mi? */
const validYm = (s: string) => /^\d{4}-\d{2}$/.test(s) && Number(s.slice(5, 7)) >= 1 && Number(s.slice(5, 7)) <= 12;

export interface BulkPeriod {
  /** `<input type="month">` değeri (ham, geçersiz de olabilir). */
  ym: string;
  setYm: (v: string) => void;
  valid: boolean;
  /** Geçerli aya düşülmüş yıl/ay — sorgular bunları kullanır. */
  year: number;
  month: number;
  /** "08/2026" */
  label: string;
  /** Tarih girdilerinin alt/üst sınırı — kayıt başka aya düşemez. */
  min: string;
  max: string;
}

/**
 * DÖNEM (ay) durumu.
 *
 * ── Neden URL'den SADECE ilk açılışta okunuyor ──
 * Sekmeler keep-alive: her açık sekme aynı anda mount kalır ve `useSearchParams` GLOBAL
 * konumu okur — yani gizli bir sekme, ÖNDEKİ sekmenin adresini görür. Doğrudan
 * `searchParams`a bağlansaydı, kullanıcı başka bir sayfaya geçtiğinde bu sayfanın ayı
 * sessizce değişirdi. Bu yüzden değer state'te tutulur ve URL yalnız BU SEKME ÖNDEYKEN
 * dinlenir (Puantaj ekranından farklı bir ayla yeniden açılabilsin diye).
 */
export function useBulkPeriod(): BulkPeriod {
  const [sp] = useSearchParams();
  const active = useTabActive();
  const spD = sp.get("d");

  const [ym, setYm] = useState<string>(() => {
    if (spD && validYm(spD)) return spD;
    const n = new Date();
    return `${n.getFullYear()}-${pad(n.getMonth() + 1)}`;
  });

  useEffect(() => {
    if (!active || !spD || !validYm(spD)) return;
    setYm(spD);
  }, [active, spD]);

  const now = new Date();
  const fallback = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
  const valid = validYm(ym);
  const safe = valid ? ym : fallback;
  const year = Number(safe.slice(0, 4));
  const month = Number(safe.slice(5, 7));
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();

  return {
    ym, setYm, valid, year, month,
    label: `${pad(month)}/${year}`,
    min: `${safe}-01`,
    max: `${safe}-${pad(lastDay)}`,
  };
}

/**
 * "Kapat" — sekmeyi kapatıp Personel/İK sayfasına döner.
 * Sekme bulunamasa bile gezinme yapılır (derin bağlantıyla açılmış olabilir).
 */
export function useCloseBulkTab(path: string): () => void {
  const navigate = useNavigate();
  const closeTab = useTabStore((s) => s.close);
  return () => {
    closeTab(path);
    navigate("/personel");
  };
}

/** Saat biçimi — TR, en çok 2 ondalık ("1,5"). */
export const fmtH = (n: number): string =>
  new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n);

/** Gün biçimi — TR, en çok 2 ondalık ("0,38"). */
export const fmtD = (n: number): string =>
  new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n);

/** Sunucudaki `.max(300)` ile AYNI olmalı — aşarsa kullanıcı ham zod JSON'u görürdü. */
export const MAX_ROWS = 300;
